import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { InternalAssessmentApiRow } from "../InternalAssessments.types";
import { LegalEntityAuditStatus } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { getSettings } from "@common/settingsProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { useAddTaskMenuItem } from "@common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks";
import { EntityType } from "@common/Common.types";

// Pairing the open dialog with its row in one value keeps the two in step, so the dialogs never have to assert
// that a row is present.
export type RowDialogKind = "delete" | "close" | "reopen" | "assignee" | "responsible" | "reminder" | "toggleReminders" | "evaluate";
export type RowDialog = { kind: RowDialogKind; row: InternalAssessmentApiRow } | undefined;

export interface UseInternalAssessmentRowActionsProps {
    refetch: () => void;
    refetchPlanLimit: () => void;
}

export default function useInternalAssessmentRowActions({ refetch, refetchPlanLimit }: UseInternalAssessmentRowActionsProps) {
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const snackbar = useDotLegalSnackbar();
    const addTaskMenuItem = useAddTaskMenuItem();

    const [dialog, setDialog] = useState<RowDialog>(undefined);

    function getMenuItems(row: InternalAssessmentApiRow) {
        let menuItems = [];

        if (row.partOfCurrentPlan) {
            if (row.isClosed || row.status === LegalEntityAuditStatus.Submitted) {
                menuItems.push({
                    menuItem: row.isClosed ? translateString("reopenInternalAssessment") : translateString("reopenCompletedInternalAssessment"),
                    onClick: () => setDialog({ kind: "reopen", row }),
                });
            }
        }

        if (!row.isClosed) {
            menuItems.push({
                menuItem: translateString("closeInternalAssessment"),
                onClick: () => setDialog({ kind: "close", row }),
            });
        }

        if (row.partOfCurrentPlan) {
            if (row.status !== LegalEntityAuditStatus.Submitted && !row.isClosed) {
                menuItems.push({
                    menuItem: translateString("sendReminder"),
                    onClick: () => setDialog({ kind: "reminder", row }),
                    disabled: row.remindersPaused,
                    tooltip: row.remindersPaused ? translateString("remindersIsPaused") : "",
                });
            }

            if (row.status !== LegalEntityAuditStatus.Submitted && !row.isClosed) {
                menuItems.push({
                    menuItem: row.remindersPaused ? translateString("resumeReminders") : translateString("pauseReminders"),
                    onClick: () => setDialog({ kind: "toggleReminders", row }),
                });
            }
        }

        if (row.canBeDeleted) {
            menuItems.push({
                menuItem: translateString("deleteInternalAssessment"),
                onClick: () => setDialog({ kind: "delete", row }),
            });
        }

        if (row.partOfCurrentPlan) {
            // The group owns the responsible and cascades it onto every assessment it holds whenever the group is
            // edited, so a per-row change here would only survive until the next edit of the group.
            if (!row.internalAssessmentGroupId) {
                menuItems.push({
                    menuItem: translateString("changeResponsible"),
                    onClick: () => setDialog({ kind: "responsible", row }),
                });
            }

            menuItems.push({
                menuItem: translateString("changeAssignee"),
                onClick: () => setDialog({ kind: "assignee", row }),
            });

            if (!row.isClosed) {
                menuItems.push({
                    menuItem: translateString("CopyLinkToAssessment"),
                    onClick: () => {
                        const url = getSettings().mainSiteBaseUrl + urlProvider.getExternalQuestionnaireResponseUrl(row.responseExternalId);
                        navigator.clipboard.writeText(url);
                        snackbar.show(translateString("linkCopied", { link: url }));
                    },
                });
            }

            // A closed assessment reports its closed reason as its status rather than Submitted, so an excluded one
            // would never match Submitted on its own. External audits evaluate on the same two conditions.
            if (row.status === LegalEntityAuditStatus.Submitted || row.isClosed) {
                menuItems.push({
                    menuItem: translateString("evaluateInternalAssessmentHeader"),
                    onClick: () => setDialog({ kind: "evaluate", row }),
                });
            }

            addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
                fromEntityId: row.id!,
                fromEntityType: EntityType.InternalAssessment,
                toEntityType: EntityType.Task,
            });
        }

        return menuItems;
    }

    const closeDialog = () => setDialog(undefined);

    return {
        getMenuItems,
        dialog,
        closeDialog,
        onDialogSaved: () => {
            closeDialog();
            refetch();
        },
        onAssessmentDeleted: () => {
            closeDialog();
            refetch();
            refetchPlanLimit();
        },
    };
}

export type InternalAssessmentRowActions = ReturnType<typeof useInternalAssessmentRowActions>;
